import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { Building2, Plus, ChevronRight } from 'lucide-react'
import { isRegistryConfirmed, isUnnamedRegistryMatch, type VerificationSource } from './NifVerificationCard'

interface Membership {
  company: {
    id: string
    name: string
    slug: string
    verifiedAt: string | null
    verificationSource?: VerificationSource
    website: string | null
  }
  role: string
}

interface CompanySelectorProps {
  memberships: Membership[]
  locale: string
}

export function CompanySelector({ memberships, locale }: CompanySelectorProps) {
  const { t } = useTranslation('employer')

  return (
    <div className="max-w-2xl mx-auto py-12">
      <div className="flex items-center gap-3 text-[#00f0ff] font-mono text-sm mb-6">
        <Building2 className="w-5 h-5" />
        <span>{t('selector.title')}</span>
      </div>
      <h1
        className="text-3xl font-black mb-8 tracking-tighter"
        style={{ fontFamily: "'Space Grotesk', sans-serif" }}
      >
        <span className="text-white">{t('selector.heading')}</span>
      </h1>

      <div className="space-y-3">
        {memberships.map(({ company, role }) => (
          <Link
            key={company.id}
            to="/$locale/employer/$companyId"
            params={{ locale, companyId: company.id }}
            className="group block border-2 border-[#00f0ff]/30 bg-[#0f0f15]/95 p-5 rounded-lg hover:border-[#00f0ff] transition-all"
          >
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-white font-mono text-lg font-bold">{company.name}</h2>
                <div className="flex items-center gap-3 mt-1">
                  <span className="text-[#a0a0b0] font-mono text-xs uppercase">{role}</span>
                  {company.verifiedAt && (
                    isRegistryConfirmed(company.verificationSource) ? (
                      <span className="text-[#39ff14] font-mono text-xs">
                        {t('overview.verified')}
                      </span>
                    ) : isUnnamedRegistryMatch(company.verificationSource) ? (
                      <span className="text-[#ff00ff] font-mono text-xs">
                        {t('overview.verifiedUnnamed')}
                      </span>
                    ) : (
                      <span className="text-[#00f0ff] font-mono text-xs">
                        {t('overview.verifiedFormat')}
                      </span>
                    )
                  )}
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-[#a0a0b0] group-hover:text-[#00f0ff] transition-colors" />
            </div>
          </Link>
        ))}
      </div>

      <Link
        to="/$locale/employer/create"
        params={{ locale }}
        className="mt-6 flex items-center gap-2 text-[#00f0ff] hover:text-[#39ff14] font-mono text-sm transition-colors"
      >
        <Plus className="w-4 h-4" />
        {t('selector.createNew')}
      </Link>
    </div>
  )
}
